import React, { useState } from 'react';

import { Modal, Input, Icon, Text, FormAlert } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

import api from '../../../utils/axios';
import { validatePassword, getPasswordCriteria } from '../../../utils/passwordValidation';

import type { IUpdatePasswordDialogProps } from './UpdatePasswordDialog.type';

export const UpdatePasswordDialog: React.FC<IUpdatePasswordDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userEmail,
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateForm = () => {
    if (!currentPassword) {
      setError('Current password is required');
      return false;
    }
    if (!newPassword) {
      setError('New password is required');
      return false;
    }
    
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0]);
      return false;
    }
    
    if (newPassword === currentPassword) {
      setError('New password must be different from current password');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/update-password-action', {
        email: userEmail,
        currentPassword,
        newPassword
      });
      
      if (response.data.success) {
        onSuccess();
        // Reset form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err: any) {
      let errorMessage = 'Failed to update password. Please try again.';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data) {
        errorMessage = typeof err.response.data === 'string' ? err.response.data : errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      onClose?.();
    }
  };

  const clearError = () => {
    if (error) setError('');
  };

  // Password strength logic
  const showPasswordPolicy = newPassword.length > 0;
  const validation = validatePassword(newPassword);
  const criteriaObj = getPasswordCriteria(newPassword);
  
  // Convert criteria object to array for rendering
  const criteria = [
    { met: criteriaObj.minLength, description: 'At least 8 characters long' },
    { met: criteriaObj.hasLowerCase, description: 'Contains lowercase letters (a-z)' },
    { met: criteriaObj.hasUpperCase, description: 'Contains uppercase letters (A-Z)' },
    { met: criteriaObj.hasNumber, description: 'Contains at least one number' },
    { met: criteriaObj.hasSpecialChar, description: 'Contains special characters (!@#$%^&*...)' }
  ];

  return (
    <Modal
      isOpen={isOpen}
      title="Password Update Required"
      size="md"
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitLabel={loading ? 'Updating...' : 'Update Password'}
      cancelLabel="Cancel"
      submitDisabled={loading || !validation.isValid}
      submitLoading={loading}
    >
      <div className="flex flex-col gap-bg-v2-lg">
        <Text variant="text-sm" color="secondary" as="p">
          Your account requires a password update for security reasons. Please enter your current password and choose a new secure password.
        </Text>

        <FormAlert
          message={error}
          variant="error"
          onDismiss={clearError}
        />

        {/* Current Password */}
        <Input
          id="currentPassword"
          name="currentPassword"
          type={showCurrentPassword ? 'text' : 'password'}
          label="Current Password"
          placeholder="Enter current password"
          required
          startIcon={<Icon name={IconName.Lock} size="md" />}
          endIcon={<Icon name={showCurrentPassword ? IconName.EyeOpen : IconName.EyeClosed} size="md" />}
          onEndIconClick={() => setShowCurrentPassword(!showCurrentPassword)}
          value={currentPassword}
          onChange={(e) => {
            setCurrentPassword(e.target.value);
            clearError();
          }}
          disabled={loading}
        />

        {/* New Password */}
        <Input
          id="newPassword"
          name="newPassword"
          type={showNewPassword ? 'text' : 'password'}
          label="New Password"
          placeholder="Enter new password"
          required
          startIcon={<Icon name={IconName.Lock} size="md" />}
          endIcon={<Icon name={showNewPassword ? IconName.EyeOpen : IconName.EyeClosed} size="md" />}
          onEndIconClick={() => setShowNewPassword(!showNewPassword)}
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            clearError();
          }}
          disabled={loading}
          showStrength={true}
        />

        {/* Confirm Password */}
        <Input
          id="confirmPassword"
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          label="Confirm New Password"
          placeholder="Re-enter new password"
          required
          startIcon={<Icon name={IconName.Lock} size="md" />}
          endIcon={<Icon name={showConfirmPassword ? IconName.EyeOpen : IconName.EyeClosed} size="md" />}
          onEndIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            clearError();
          }}
          disabled={loading}
        />
      </div>
    </Modal>
  );
};
